import { ReactNode, createContext, useContext, useMemo, useState } from "react";
import { useUserProfile } from "@rentablo/api/apiv1";
import { DEFAULT_CURRENCY } from "@constants";

export type CurrencyToggleMode = "original" | "global";

interface CurrencyDisplayContextValue {
  globalCurrency: string;
  isLoading: boolean;
  selectedCurrencyView: CurrencyToggleMode;
  setSelectedCurrencyView: (view: CurrencyToggleMode) => void;
  getDisplayCurrency: (originalCurrency: string | undefined) => string;
}

const CurrencyDisplayContext =
  createContext<CurrencyDisplayContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
  initialView?: CurrencyToggleMode;
}

const DEFAULT_VIEW: CurrencyToggleMode = "original";

export const CurrencyDisplayProvider = ({
  children,
  initialView = DEFAULT_VIEW,
}: ProviderProps) => {
  const { data: userProfile, isLoading } = useUserProfile();
  const globalCurrency = userProfile?.reportingCurrency ?? DEFAULT_CURRENCY;

  const [selectedCurrencyView, setSelectedCurrencyView] =
    useState<CurrencyToggleMode>(initialView);

  const value = useMemo<CurrencyDisplayContextValue>(
    () => ({
      globalCurrency,
      isLoading,
      selectedCurrencyView,
      setSelectedCurrencyView,
      getDisplayCurrency: (originalCurrency) =>
        selectedCurrencyView === "original"
          ? (originalCurrency ?? globalCurrency)
          : globalCurrency,
    }),
    [globalCurrency, isLoading, selectedCurrencyView]
  );

  return (
    <CurrencyDisplayContext.Provider value={value}>
      {children}
    </CurrencyDisplayContext.Provider>
  );
};

export const useCurrencyDisplay = (): CurrencyDisplayContextValue => {
  const ctx = useContext(CurrencyDisplayContext);
  if (ctx) return ctx;
  return {
    globalCurrency: DEFAULT_CURRENCY,
    isLoading: false,
    selectedCurrencyView: "original",
    setSelectedCurrencyView: () => {},
    getDisplayCurrency: (originalCurrency) =>
      originalCurrency ?? DEFAULT_CURRENCY,
  };
};
